import { ReactNode, useEffect, useMemo, useRef } from 'react'
import {
  NumberFormatBase,
  numericFormatter,
  removeNumericFormat,
  getNumericCaretBoundary,
  NumericFormatProps,
  NumberFormatValues
} from 'react-number-format'
import { useMsg } from '~/common/localizationUtils'
import { PAY_TYPE_DISPLAY_FORMAT_MAP, PAY_TYPE_FORMAT_MAP, PayType, PayTypeDisplay } from '~/api/jobs'
import { Currency, useCurrenciesQuery } from '../../../api/currency'
import { useFormatCurrencyLocale, useFormatCurrencyRangeLocale } from '../../../common/currencyUtils'
import createDebugLogger from '../../../common/logger'
import { useIntlContext } from '../../../components/IntlProviders.context'
import { useStateRef } from '../../../hooks/useStateRef'
import {
  Box,
  forwardRef,
  Input,
  InputGroup,
  InputLeftAddon,
  InputLeftElement,
  InputProps,
  InputRightAddon,
  InputRightElement,
  useFormControlContext
} from '../../chakra'

const debug = createDebugLogger('InputCurrency')

export type CurrencyDisplayProps = {
  value?: string | number | null
  currency?: string
  fullPrecision?: boolean
}

type RateDisplayProps = {
  value?: string | number | null
  currency?: string
  fullPrecision?: boolean
  rateType?: PayType | PayTypeDisplay | null
  longForm?: boolean
}

type RateRangeDisplayProps = {
  minValue?: string | number | null
  maxValue?: string | number | null
  currency?: string
  rateType?: PayType | PayTypeDisplay | null
  longForm?: boolean
}

/**
 * CurrencyDisplay
 *
 * A component to display currency amounts. Values are formatted based on the user's locale, the currency code,
 * and the maximum number of decimal places is controlled by Currency Settings (or can be overridden by the fullPrecision prop).
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-currency-display--docs
 */
export function CurrencyDisplay({ value, currency, fullPrecision }: CurrencyDisplayProps) {
  const formatCurrencyLocale = useFormatCurrencyLocale()
  if (value === '' || value == null || Number.isNaN(Number(value))) return null
  return <>{formatCurrencyLocale(String(value), { currency, maxDecimals: fullPrecision ? 20 : undefined })}</>
}

export function RateDisplay({ value, currency, fullPrecision, rateType, longForm = false }: RateDisplayProps) {
  const formatCurrencyLocale = useFormatCurrencyLocale()
  const msg = useMsg()

  if (value === '' || value == null || Number.isNaN(Number(value))) return null

  const formattedCurrency = formatCurrencyLocale(String(value), {
    currency,
    maxDecimals: fullPrecision ? 20 : undefined
  })
  if (rateType && !longForm) {
    const format = PAY_TYPE_FORMAT_MAP[rateType]
    if (format) {
      return <>{msg(format, { value: formattedCurrency })}</>
    }
  } else if (rateType && longForm) {
    const format = PAY_TYPE_DISPLAY_FORMAT_MAP[rateType]
    if (format) return <>{msg(format, { value: formattedCurrency })}</>
  }
  return <>{formattedCurrency}</>
}

export function CurrencyRangeDisplay({
  minValue,
  maxValue,
  currency
}: {
  minValue: string | number | null | undefined
  maxValue: string | number | null | undefined
  currency: string
  maximumFractionDigits?: number
}) {
  const formatCurrencyRangeLocale = useFormatCurrencyRangeLocale()
  if (
    minValue === '' ||
    minValue == null ||
    Number.isNaN(Number(minValue)) ||
    maxValue === '' ||
    maxValue == null ||
    Number.isNaN(Number(maxValue))
  )
    return null
  return <>{formatCurrencyRangeLocale(String(minValue), String(maxValue), { currency })}</>
}

export function RateRangeDisplay({ minValue, maxValue, currency, rateType, longForm = false }: RateRangeDisplayProps) {
  const formatCurrencyRangeLocale = useFormatCurrencyRangeLocale()
  const msg = useMsg()

  let min = minValue
  let max = maxValue
  const minIsValid = min !== null && min !== undefined && !Number.isNaN(Number(min))
  const maxIsValid = max !== null && max !== undefined && !Number.isNaN(Number(max))

  if (!minIsValid && !maxIsValid) {
    return <></>
  }

  if (minIsValid && !maxIsValid) {
    max = 0
  } else if (!minIsValid && maxIsValid) {
    min = 0
  }

  const formattedRange = formatCurrencyRangeLocale(String(min), String(max), { currency })
  if (rateType && !longForm) {
    const format = PAY_TYPE_FORMAT_MAP[rateType]
    if (format) {
      return <>{msg(format, { value: formattedRange })}</>
    }
  } else if (rateType && longForm) {
    const format = PAY_TYPE_DISPLAY_FORMAT_MAP[rateType]
    if (format) return <>{msg(format, { value: formattedRange })}</>
  }
  return <>{formattedRange}</>
}

/** Truncate a raw numeric string's decimals to `maxDecimals` (v5's removeNumericFormat doesn't). */
function clampDecimals(numStr: string, maxDecimals: number): string {
  const dotIndex = numStr.indexOf('.')
  if (dotIndex === -1) return numStr
  return numStr.slice(0, dotIndex + 1 + maxDecimals)
}

type onChangeFloat = (value: number | null) => void
type onChangeString = (value: string) => void
export type InputCurrencyProps = {
  currency: string
  min?: number
  max?: number
  fullPrecision?: number
  'data-testid'?: string
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  /**
   * @deprecated Use the <CurrencyDisplay /> component instead of setting this prop to 'text' on this component
   */
  displayType?: 'input' | 'text'
  /** Rendered as a real sibling addon in the input's group, so the input's adjoining corner is squared. */
  leftAddon?: ReactNode
  rightAddon?: ReactNode
} & Omit<NumericFormatProps<InputProps>, 'onChange' | 'customInput' | 'prefix' | 'suffix' | 'displayType'> &
  // This code makes it so that if valueAsNumber is true,
  // then onChange must be a function that takes a number or null
  // and if valueAsNumber is false, then onChange must be a function that takes a string
  ({ valueAsNumber: true; onChange?: onChangeFloat } | { valueAsNumber?: false | undefined; onChange?: onChangeString })

/**
 * InputCurrency
 *
 * Form control input to enter currency amounts. Values are formatted based on the user's locale
 * and currency code. The currency code will be displayed with the value and positioned based on
 * the ISO standard.
 *
 * All currency is displayed using the react-number-format component.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-currency-input--docs
 */
export const InputCurrency = forwardRef<InputCurrencyProps, 'input'>(
  (
    {
      currency,
      value,
      defaultValue,
      displayType,
      min = 0,
      max = 1e20,
      inputMode = 'decimal',
      'data-testid': dataTestId,
      onChange,
      onBlur,
      valueAsNumber,
      fullPrecision,
      leftAddon,
      rightAddon,
      ...rest
    },
    ref
  ) => {
    const isControlled = value !== undefined

    debug('---- render ----')
    const { id, isDisabled, isReadOnly } = useFormControlContext() ?? {}
    // We will store all three value types from react-number-format's onChange in state as they are all handy to have around
    const [values, setValues] = useStateRef<NumberFormatValues>(() => {
      return {
        value: '',
        floatValue: undefined,
        formattedValue: ''
      }
    })

    const resolvedOpts = useResolveCurrencyFormat({ currencyCode: currency })

    const prevValuesRef = useRef<NumberFormatValues>(values.current)
    if (prevValuesRef.current.formattedValue !== values.current.formattedValue) {
      debug('values changed', { values: values.current, prevValues: prevValuesRef.current })
      prevValuesRef.current = values.current
    }

    // We'll keep track of the previous value, so we can compare it to the current value
    // and update the internal state if the value has changed. Using the usePrevious hook
    // didn't work here because I need to update the previous value sooner than the next render.
    const prevValueRef = useRef<string | number | null | undefined>(undefined)

    // The exact value we last handed to onChange (same scalar shape the parent controls with). When the
    // `value` prop comes back equal to it, it's the parent echoing the user's in-progress typing — don't
    // pad, or "5.1" would snap to "5.10". A value that differs is external (initial, or a
    // derived/calculated value like a bill rate) and SHOULD be padded ("200" -> "200.00").
    const lastEmittedRef = useRef<string | number | null | undefined>(undefined)

    debug('Render', id, {
      value,
      prevValue: prevValueRef.current,
      values: values.current,
      resolvedOpts: {
        locale: resolvedOpts.locale,
        currency: resolvedOpts.currency,
        minimumFractionDigits: resolvedOpts.minimumFractionDigits,
        maximumFractionDigits: resolvedOpts.maximumFractionDigits,
        groupDelimiter: resolvedOpts.groupDelimiter,
        decimalDelimiter: resolvedOpts.decimalDelimiter,
        currencyPosition: resolvedOpts.currencyPosition
      }
    })

    // We'll store the largest decimal scale seen since mount, so we can use it as the minimum decimal scale
    // Basically if a value/defaultValue is programmatically provided that has a higher precision than the
    // one from the Currency settings, we'll just honor it.
    const largestDecimalScaleSeenSinceMount = useRef(0)

    // Significant decimals only — API Decimals often arrive as "100.0000000"; trailing zeros
    // shouldn't inflate the allowed scale past what the value actually needs.
    const fractionDigits =
      String(value || defaultValue || 0)
        .split('.')[1]
        ?.replace(/0+$/, '') ?? ''
    const decimalsFromValue = Math.min(fractionDigits.length, 20)
    const decimalScale = Math.max(
      decimalsFromValue,
      fullPrecision ? 20 : resolvedOpts.maximumFractionDigits ?? 0,
      largestDecimalScaleSeenSinceMount.current
    )
    largestDecimalScaleSeenSinceMount.current = decimalScale

    const formatCurrencyLocale = useFormatCurrencyLocale()

    // Builds the { value, floatValue, formattedValue } triple from a raw value. Options:
    //  - clamp:    restrict to min/max (blur only).
    //  - pad:      fill out to the currency's min decimals ("5" -> "5.00"); defaults to clamp. Never
    //              wanted mid-typing — it would snap "5.1" to "5.10".
    //  - preserve: keep the caller's exact decimal string (echo of in-progress typing only).
    //              Default normalizes through Number, dropping junk trailing zeros from API
    //              strings ("100.0000000" -> "100"), then pad restores currency mins ("100.00").
    function makeValuesFromValue(
      v: string | number | null | undefined,
      { clamp = false, pad = clamp, preserve = false }: { clamp?: boolean; pad?: boolean; preserve?: boolean } = {}
    ) {
      if (v === '' || v == null) {
        return {
          value: '',
          floatValue: undefined,
          formattedValue: ''
        }
      }
      // So it will allow the users to enter negative numbers
      if (v === '-') {
        return {
          value: '-',
          floatValue: undefined,
          formattedValue: '-'
        }
      }
      // `num` is the numeric value: it always drives floatValue and the formatted display, and is
      // clamped to min/max on blur.
      const num = clamp ? Math.min(Math.max(Number(v), min), max) : Number(v)

      // `value` is the raw string we store and feed the input (we own its exact decimals — the lib
      // never reformats it). Take an exact-decimal string (the caller's verbatim when `preserve`, else
      // the number normalized), then pad with zeros only when asked AND it falls short of the min — so
      // a high-precision value that already clears the min is never truncated.
      const minFrac = resolvedOpts.minimumFractionDigits ?? 0
      const exactString = preserve ? String(v) : String(num)
      const isShortOnDecimals = (exactString.split('.')[1]?.length ?? 0) < minFrac
      const valueString = pad && isShortOnDecimals ? num.toFixed(minFrac) : exactString

      return {
        value: valueString,
        floatValue: num,
        formattedValue: formatCurrencyWithoutCode(num)
      }

      // formatCurrencyWithoutCode: excludes the currency code
      //    Used for the input's value when displayType is 'input', the code isn't needed in the formatted output
      //    because it's displayed as a prefix or suffix using InputLeftElement or InputRightElement
      function formatCurrencyWithoutCode(v: string | number | null | undefined) {
        if (v === '' || v == null) return ''
        debug('formatCurrencyWithoutCode', id, v, { currency, maxDecimals: largestDecimalScaleSeenSinceMount.current })
        return formatCurrencyLocale(v, { currency, maxDecimals: largestDecimalScaleSeenSinceMount.current })
          .replace(currency, '')
          .trim()
      }
    }

    // If the value prop (aka externally controlled value) has changed, we need to update the internal state
    const controlledValueChanged = value !== prevValueRef.current
    const valueIsDifferentFromStoredValue = valueAsNumber
      ? value !== values.current.floatValue
      : value !== values.current.value
    if (isControlled && controlledValueChanged && valueIsDifferentFromStoredValue) {
      // Echo of our own emit (in-progress typing): preserve the partial string, don't pad —
      // otherwise "5.1" snaps to "5.10". External values (initial API string, derived rate):
      // normalize junk trailing zeros via Number, then pad to the currency min
      // ("100.0000000" -> "100.00").
      const isEchoOfOurEmit = value === lastEmittedRef.current
      const newValues = makeValuesFromValue(value, {
        pad: !isEchoOfOurEmit,
        preserve: isEchoOfOurEmit
      })
      debug('value HasChanged', id, {
        value,
        prevValue: prevValueRef.current,
        outOfSync: valueIsDifferentFromStoredValue,
        prevValues: values.current,
        newValues
      })
      setValues(newValues)
      prevValueRef.current = value
    }

    useEffect(() => {
      if (defaultValue !== undefined) {
        const values = makeValuesFromValue(defaultValue, { pad: true })
        debug('defaultValue set into values', id, values)
        setValues(values)
      }
    }, [])

    function internalOnChange(newValues: NumberFormatValues) {
      debug('internalOnChange', id, newValues)

      // If uncontrolled, update the internal state
      if (!isControlled) setValues(newValues)

      // Emit, and record exactly what we hand the parent so the controlled-value sync can recognize the
      // parent echoing it back (in-progress typing — don't pad) vs an external value (pad).
      if (valueAsNumber) {
        const emitted = newValues.floatValue ?? null
        lastEmittedRef.current = emitted
        ;(onChange as onChangeFloat)?.(emitted)
      } else {
        lastEmittedRef.current = newValues.value
        ;(onChange as onChangeString)?.(newValues.value)
      }
    }

    const formControlCtx = useFormControlContext()

    // Shared by NumberFormatBase's three string<->display hooks below. We drive all of them with v5's
    // own helpers (numericFormatter / removeNumericFormat / getNumericCaretBoundary) so display, parse,
    // and caret math stay consistent. decimalScale is omitted on purpose so numericFormatter preserves
    // the stored string's exact decimals (trailing zeros, partial entries); we own min/max padding in
    // makeValuesFromValue and decimal truncation in clampDecimals.
    const numberFormatProps = {
      decimalSeparator: resolvedOpts.decimalDelimiter,
      thousandSeparator: resolvedOpts.groupDelimiter,
      prefix: '',
      suffix: '',
      allowNegative: true
    }

    if (displayType === 'text') {
      return (
        <Box aria-labelledby={formControlCtx?.labelId}>
          <CurrencyDisplay value={value} currency={currency} />
        </Box>
      )
    }

    return (
      <InputGroup>
        {leftAddon && <InputLeftAddon>{leftAddon}</InputLeftAddon>}
        {/* Positioned wrapper keeps the currency code element inside the input, clear of any addon */}
        <Box position="relative" flex="1" minW={0}>
          {resolvedOpts.currencyPosition === 'prefix' && (
            <InputLeftElement data-currency-code pointerEvents="none" pl={4} opacity={isDisabled ? '0.4' : '1'}>
              {currency}
            </InputLeftElement>
          )}
          {/* @ts-ignore */}
          <NumberFormatBase
            id={id}
            data-currency-input
            // We feed v5 the raw numeric string we maintain and own the display via `format`, so v5
            // never reformats (which jammed non-en typing) nor strips the cents we pad on blur. Parsing
            // and caret math reuse v5's own removeNumericFormat/getNumericCaretBoundary (configured with
            // this locale's separators), so mid-string edits stay correct.
            value={values.current.value}
            valueIsNumericString
            format={(numStr: string) => numericFormatter(numStr, numberFormatProps)}
            removeFormatting={(formattedValue: string, changeMeta) =>
              // NumberFormatBase always supplies changeMeta at runtime; its type just marks it optional.
              clampDecimals(removeNumericFormat(formattedValue, changeMeta!, numberFormatProps), decimalScale)
            }
            getCaretBoundary={(formattedValue: string) => getNumericCaretBoundary(formattedValue, numberFormatProps)}
            customInput={Input}
            getInputRef={ref}
            data-testid={dataTestId}
            isReadOnly={isReadOnly}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              // onBlur if you've only typed, e.g. 55.1 then the value is changed to 55.10
              // We do leave empty string as empty string though, we don't want to format to 0.00 for empty.
              const newValues = makeValuesFromValue(values.current.value, { clamp: true })
              debug('onBlur', id, values.current.value, '->', newValues)
              internalOnChange(newValues)
              // In valueAsNumber mode the parent echoes back a number (e.g. 5), which can't carry the cents
              // we just padded ("5.00"), and since it equals what the parent already held no controlled-sync
              // fires to restore them — so reflect the padded display locally. (String mode rides the echo.)
              if (isControlled && valueAsNumber) setValues(newValues)
              onBlur?.(e)
            }}
            onValueChange={(values, sourceInfo) => {
              debug('onValueChange', id, values, sourceInfo.source)
              // While actively typing we allow invalid partial entries such as 55.1
              // which is missing another decimal place. But the user is still typing.
              // We also do not clamp, aka we don't use makeValuesFromValue, during
              // onValueChange, we wait until blur to clamp if needed.
              internalOnChange(values)
            }}
            // Chakra's InputGroup only squares corners on children flagged as Input; ours renders NumberFormatBase
            sx={{
              ...(resolvedOpts.currencyPosition === 'prefix' ? { paddingLeft: '3rem' } : { paddingRight: '3rem' }),
              borderStartRadius: leftAddon ? 0 : undefined,
              borderEndRadius: rightAddon ? 0 : undefined
            }}
            inputMode={inputMode}
            {...rest}
          />
          {resolvedOpts.currencyPosition === 'suffix' && (
            <InputRightElement data-currency-code pointerEvents="none" pr={4} opacity={isDisabled ? '0.4' : '1'}>
              {currency}
            </InputRightElement>
          )}
        </Box>
        {rightAddon && <InputRightAddon>{rightAddon}</InputRightAddon>}
      </InputGroup>
    )
  }
)

InputCurrency.displayName = 'InputCurrency'

/**
 * Hook to return all the parts of the currency formats needed to display in an input
 *
 * {
 *   'locale': 'en-US',
 *   'numberingSystem': 'latn',
 *   'style': 'currency',
 *   'currency': 'USD',
 *   'currencyDisplay': 'symbol',
 *   'minimumIntegerDigits': 1,
 *   'minimumFractionDigits': 2,
 *   'maximumFractionDigits': 2,
 *   'useGrouping': 'auto',
 *   'groupDelimiter': '.',
 *   'decimalDelimiter': ',',
 *   'currencySymbol': "USD"
 *   'currencyPosition': 'prefix',
 *   'notation': "standard",
 *   'roundingIncrement': 1
 *   'roundingMode': "halfExpand"
 *   'roundingPriority': "auto"
 *   'signDisplay': "auto"
 *   'trailingZeroDisplay':"auto"
 * }
 */
export const useResolveCurrencyFormat = ({ currencyCode }: { currencyCode: string }) => {
  const intl = useIntlContext().intl
  const currency = useCurrenciesQuery().data?.find((c: Currency) => c.code === currencyCode)

  return useMemo(() => {
    const numberFormat = new Intl.NumberFormat(intl.locale, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'code'
    })

    // Just give it a number that needs grouping and decimal formatting, so we can find out which
    // characters are used for grouping and decimal.
    const parts = numberFormat.formatToParts(Number(11111111.11111))
    const resolvedOptions = numberFormat.resolvedOptions()

    if (currency?.decimal_places !== undefined) {
      resolvedOptions.maximumFractionDigits = currency.decimal_places
    }

    const groupDelimiter = getPart(parts, 'group')
    let decimalDelimiter = getPart(parts, 'decimal')

    // if the decimal places allowed for given currency is zero, the decimal delimiter
    // must not be set to the same value as the group delimiter or undefined. Otherwise,
    // the react-number-format library will throw an error. The code below is ensuring
    // that it is set to a different value.
    if (resolvedOptions.maximumFractionDigits === 0 || !decimalDelimiter) {
      if (groupDelimiter === ',') {
        decimalDelimiter = '.'
      } else {
        decimalDelimiter = ','
      }
    }

    const currencySymbol = getPart(parts, 'currency')
    const currencyIndex = parts.findIndex(part => part.type === 'currency')
    const currencyPosition = currencyIndex <= 1 ? 'prefix' : 'suffix'

    return {
      ...resolvedOptions,
      groupDelimiter,
      decimalDelimiter,
      currencySymbol,
      currencyPosition
    }
  }, [currencyCode, currency, intl])
}

function getPart(parts: Intl.NumberFormatPart[], name: Intl.NumberFormatPart['type']): string | undefined {
  return parts.find(part => part.type === name)?.value
}
